import { useCallback, useState } from 'react';
import { createFileRoute, Link, useBlocker } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { requireFeature } from '@/common/utils/requireFeature';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { projectTemplatesService } from '@/features/tech-systems/project-templates/services/project-templates.service';
import { projectTemplatesKeys } from '@/features/tech-systems/project-templates/constants';
import { TemplateEditor } from '@/features/tech-systems/project-templates/components/TemplateEditor';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  canManageProjectTemplates,
  canViewProjectTemplates,
} from '@/features/tech-systems/project-templates/utils/access';

export const Route = createFileRoute('/_authenticated/tech-systems/templates/$id')({
  beforeLoad: () => requireFeature('TECH_SYSTEMS'),
  component: ProjectTemplateEditorPage,
});

function ProjectTemplateEditorPage() {
  const { id } = Route.useParams();
  const { hasPermission } = usePermissions();
  const canView = canViewProjectTemplates(hasPermission);
  const canManage = canManageProjectTemplates(hasPermission);
  const [dirty, setDirty] = useState(false);
  const handleDirtyChange = useCallback((value: boolean) => setDirty(value), []);

  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  const { data: template, isLoading, isError } = useQuery({
    queryKey: projectTemplatesKeys.detail(id),
    queryFn: () => projectTemplatesService.get(id),
    enabled: canView,
    // Evita sobrescrever o rascunho com refetch em foco.
    refetchOnWindowFocus: false,
  });

  if (!canView) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-2xl font-semibold">Template de projeto</h1>
        <p className="text-sm text-muted-foreground">
          Você não tem permissão para acessar os templates de projeto dos Sistemas TEC.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tech-systems/templates">
            <ArrowLeft className="h-4 w-4" />
            Templates
          </Link>
        </Button>
        <h1 className="min-w-0 truncate text-2xl font-semibold">
          {template?.name ?? 'Template de projeto'}
        </h1>
        {!canManage && <Badge variant="secondary">Somente leitura</Badge>}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Carregando template...</p>
      ) : isError || !template ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Template não encontrado ou sem acesso.
          </CardContent>
        </Card>
      ) : (
        <TemplateEditor
          key={template.id}
          template={template}
          readOnly={!canManage}
          onDirtyChange={handleDirtyChange}
        />
      )}

      <ConfirmDialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open && blocker.status === 'blocked') blocker.reset();
        }}
        title="Descartar alterações?"
        description="Há alterações não salvas neste template. Se sair agora, elas serão perdidas."
        confirmLabel="Sair sem salvar"
        cancelLabel="Continuar editando"
        destructive
        onConfirm={() => blocker.status === 'blocked' && blocker.proceed()}
      />
    </div>
  );
}
